<template>
  <div class="flow-records-admin">
    <div class="page-header">
      <h2>流水管理</h2>
      <p>对消费 / 充值 / 设备使用 / 商品订单 / 库存变动 五类流水进行查询与软删除（仅超级管理员）</p>
    </div>

    <div class="page-content">
      <div class="tab-bar">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          :class="['tab-item', { active: currentType === tab.key }]"
          @click="switchType(tab.key)"
        >
          {{ tab.name }}
        </button>
      </div>

      <div class="search-section">
        <div class="search-box">
          <select v-model="searchStoreId" class="store-select">
            <option value="">全部门店</option>
            <optgroup v-for="group in $groupStoresByCity(storeList)" :key="group.city" :label="group.city">
              <option v-for="store in group.stores" :key="store.id" :value="store.id">
                {{ $formatStoreLabel(store, false) }}
              </option>
            </optgroup>
          </select>
          <input
            v-model="searchStartTime"
            type="datetime-local"
            class="date-input"
            placeholder="开始时间"
          />
          <input
            v-model="searchEndTime"
            type="datetime-local"
            class="date-input"
            placeholder="结束时间"
          />
          <input
            v-model="searchKeyword"
            type="text"
            placeholder="关键字（客户/订单号/设备/操作人）"
            class="search-input"
            @keyup.enter="handleSearch"
          />
          <label class="check-label">
            <input v-model="includeDeleted" type="checkbox" />
            包含已删除
          </label>
          <button @click="handleSearch" class="search-btn">查询</button>
          <button @click="handleReset" class="reset-btn">重置</button>
        </div>

        <div class="action-bar">
          <button
            class="danger-btn"
            :disabled="!canRangeDelete"
            @click="confirmDeleteByRange"
            title="按当前选择的时间段（必填），将该范围内所有未删除流水软删"
          >
            按当前时间段全部软删除
          </button>
          <button
            class="danger-btn outline"
            :disabled="selectedIds.length === 0"
            @click="confirmDeleteSelected"
          >
            批量删除已选 {{ selectedIds.length }} 条
          </button>
          <span class="hint">软删除后该数据在常规列表（消费记录/充值记录等）不再显示，可联系超级管理员从数据库恢复。</span>
        </div>
      </div>

      <div class="table-section">
        <div class="table-header">
          <h3>{{ currentTab.name }}列表</h3>
        </div>

        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th class="check-col">
                  <input
                    type="checkbox"
                    :checked="allSelectedOnPage"
                    :disabled="!hasSelectableRows"
                    @change="toggleSelectAllOnPage($event.target.checked)"
                  />
                </th>
                <th>ID</th>
                <th>发生时间</th>
                <th>门店</th>
                <th>摘要</th>
                <th>金额</th>
                <th>状态</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading" class="loading-row">
                <td colspan="8" class="loading-cell">
                  <div class="loading-spinner">
                    <div class="spinner"></div>
                  </div>
                </td>
              </tr>
              <tr v-else-if="!recordList || recordList.length === 0" class="empty-row">
                <td colspan="8" class="empty-cell">暂无数据</td>
              </tr>
              <tr v-else v-for="record in recordList" :key="record.id" class="data-row" :class="{ 'row-deleted': record.deleted }">
                <td class="check-col">
                  <input
                    type="checkbox"
                    :checked="isSelected(record.id)"
                    :disabled="record.deleted"
                    @change="toggleSelect(record.id, $event.target.checked)"
                  />
                </td>
                <td>{{ record.id }}</td>
                <td>{{ formatDate(record.occurTime) }}</td>
                <td>{{ getStoreLabel(record.storeId, record.storeName) }}</td>
                <td class="summary-cell">{{ record.summary || '-' }}</td>
                <td class="amount-cell">{{ record.amount == null ? '-' : formatAmount(record.amount) + '元' }}</td>
                <td>
                  <span :class="['status-badge', record.deleted ? 'inactive' : 'active']">
                    {{ record.deleted ? '已删除' : '正常' }}
                  </span>
                </td>
                <td>
                  <button
                    class="row-delete-btn"
                    :disabled="record.deleted"
                    @click="confirmDeleteOne(record)"
                  >
                    删除
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="pagination-section" v-if="!loading && recordList && recordList.length > 0">
          <div class="pagination-info">
            共 {{ pagination.totalElements }} 条记录，第 {{ pagination.number + 1 }} / {{ pagination.totalPages }} 页
          </div>
          <div class="pagination-controls">
            <button
              @click="handlePageChange(0)"
              :disabled="pagination.first"
              class="page-btn"
            >首页</button>
            <button
              @click="handlePageChange(pagination.number - 1)"
              :disabled="pagination.first"
              class="page-btn"
            >上一页</button>
            <span class="page-info">
              {{ pagination.number + 1 }} / {{ pagination.totalPages }}
            </span>
            <button
              @click="handlePageChange(pagination.number + 1)"
              :disabled="pagination.last"
              class="page-btn"
            >下一页</button>
            <button
              @click="handlePageChange(pagination.totalPages - 1)"
              :disabled="pagination.last"
              class="page-btn"
            >末页</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

const TAB_LIST = [
  { key: 'consumption', name: '消费记录' },
  { key: 'recharge', name: '充值记录' },
  { key: 'device-usage', name: '设备使用' },
  { key: 'item-order', name: '商品订单' },
  { key: 'stock-movement', name: '库存变动' }
]

export default {
  name: 'FlowRecordsAdmin',
  data() {
    return {
      tabs: TAB_LIST,
      currentType: 'consumption',
      storeList: [],
      recordList: [],
      loading: false,
      searchStoreId: '',
      searchStartTime: '',
      searchEndTime: '',
      searchKeyword: '',
      includeDeleted: false,
      selectedIdMap: {},
      pagination: {
        number: 0,
        size: 20,
        totalElements: 0,
        totalPages: 0,
        first: true,
        last: true
      }
    }
  },
  computed: {
    currentTab() {
      return this.tabs.find(t => t.key === this.currentType) || this.tabs[0]
    },
    selectedIds() {
      return Object.keys(this.selectedIdMap).filter(id => this.selectedIdMap[id]).map(id => Number(id))
    },
    canRangeDelete() {
      return !!(this.searchStartTime && this.searchEndTime)
    },
    selectableRowsOnPage() {
      return (this.recordList || []).filter(r => !r.deleted)
    },
    hasSelectableRows() {
      return this.selectableRowsOnPage.length > 0
    },
    allSelectedOnPage() {
      return this.selectableRowsOnPage.length > 0
        && this.selectableRowsOnPage.every(r => this.selectedIdMap[r.id])
    }
  },
  mounted() {
    this.loadStoreList()
    this.loadRecordList()
  },
  methods: {
    async loadStoreList() {
      try {
        const response = await request.get('/api/stores/getStores', {
          params: { page: 0, size: 1000 }
        })
        if (response.data.success && response.data.code === 200) {
          this.storeList = response.data.data.content || []
        }
      } catch (error) {
        console.error('获取门店列表失败:', error)
      }
    },
    getStoreLabel(storeId, fallbackName = '') {
      const store = this.storeList.find(item => String(item.id) === String(storeId))
      return store ? this.$formatStoreLabel(store) : (fallbackName || '-')
    },
    switchType(type) {
      if (type === this.currentType) return
      this.currentType = type
      this.pagination.number = 0
      this.selectedIdMap = {}
      this.loadRecordList()
    },
    async loadRecordList() {
      this.loading = true
      try {
        const url = `/api/admin/flow-records/${this.currentType}/query?page=${this.pagination.number}&size=${this.pagination.size}`
        const response = await request.post(url, {
          storeId: this.searchStoreId || null,
          startTime: this.normalizeTime(this.searchStartTime),
          endTime: this.normalizeTime(this.searchEndTime),
          keyword: this.searchKeyword || null,
          includeDeleted: this.includeDeleted
        })

        if (response.data.success && response.data.code === 200) {
          const data = response.data.data || {}
          this.recordList = data.content || []
          this.pagination = {
            number: data.number || 0,
            size: data.size || 20,
            totalElements: data.totalElements || 0,
            totalPages: data.totalPages || 0,
            first: !!data.first,
            last: !!data.last
          }
        } else {
          throw new Error(response.data.message || '查询失败')
        }
      } catch (error) {
        console.error('查询流水失败:', error)
        alert(this.extractError(error, '查询流水失败'))
        this.recordList = []
        this.pagination = { number: 0, size: 20, totalElements: 0, totalPages: 0, first: true, last: true }
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      if (this.searchStartTime && !this.searchEndTime) {
        alert('请选择结束时间')
        return
      }
      if (this.searchEndTime && !this.searchStartTime) {
        alert('请选择开始时间')
        return
      }
      this.pagination.number = 0
      this.selectedIdMap = {}
      this.loadRecordList()
    },
    handleReset() {
      this.searchStoreId = ''
      this.searchStartTime = ''
      this.searchEndTime = ''
      this.searchKeyword = ''
      this.includeDeleted = false
      this.pagination.number = 0
      this.selectedIdMap = {}
      this.loadRecordList()
    },
    handlePageChange(pageNumber) {
      if (pageNumber >= 0 && pageNumber < this.pagination.totalPages) {
        this.pagination.number = pageNumber
        this.loadRecordList()
      }
    },
    isSelected(id) {
      return !!this.selectedIdMap[id]
    },
    toggleSelect(id, checked) {
      if (checked) {
        this.selectedIdMap = { ...this.selectedIdMap, [id]: true }
      } else {
        const next = { ...this.selectedIdMap }
        delete next[id]
        this.selectedIdMap = next
      }
    },
    toggleSelectAllOnPage(checked) {
      const next = { ...this.selectedIdMap }
      this.selectableRowsOnPage.forEach(r => {
        if (checked) {
          next[r.id] = true
        } else {
          delete next[r.id]
        }
      })
      this.selectedIdMap = next
    },
    confirmDeleteOne(record) {
      if (!record || record.deleted) return
      const msg = `确认对该条 ${this.currentTab.name}（ID: ${record.id}）执行软删除？\n该数据将不在常规列表中显示，可联系超级管理员从数据库恢复。`
      if (!confirm(msg)) return
      this.executeDeleteByIds([record.id])
    },
    confirmDeleteSelected() {
      if (this.selectedIds.length === 0) return
      const msg = `将对已选中的 ${this.selectedIds.length} 条 ${this.currentTab.name} 执行软删除？\n该数据将不在常规列表中显示，可联系超级管理员从数据库恢复。`
      if (!confirm(msg)) return
      this.executeDeleteByIds(this.selectedIds)
    },
    confirmDeleteByRange() {
      if (!this.canRangeDelete) {
        alert('请先选择开始时间和结束时间')
        return
      }
      const storeName = this.searchStoreId
        ? this.$formatStoreLabel(this.storeList.find(s => s.id === Number(this.searchStoreId)))
        : '全部门店'
      const msg = `将对【${storeName}】在 ${this.searchStartTime} ~ ${this.searchEndTime} 范围内的所有未删除 ${this.currentTab.name} 执行软删除？\n该范围内的数据将不在常规列表中显示，可联系超级管理员从数据库恢复。\n\n此操作不可在前端撤销，请再次确认。`
      if (!confirm(msg)) return
      const second = prompt('请输入"确认删除"以执行：')
      if (second !== '确认删除') {
        alert('已取消')
        return
      }
      this.executeDeleteByRange()
    },
    async executeDeleteByIds(ids) {
      try {
        const url = `/api/admin/flow-records/${this.currentType}/delete-by-ids`
        const response = await request.post(url, { ids })
        if (response.data.success && response.data.code === 200) {
          alert(response.data.message || '删除成功')
          this.selectedIdMap = {}
          this.loadRecordList()
        } else {
          throw new Error(response.data.message || '删除失败')
        }
      } catch (error) {
        console.error('删除流水失败:', error)
        alert(this.extractError(error, '删除失败'))
      }
    },
    async executeDeleteByRange() {
      try {
        const url = `/api/admin/flow-records/${this.currentType}/delete-by-range`
        const response = await request.post(url, {
          storeId: this.searchStoreId || null,
          startTime: this.normalizeTime(this.searchStartTime),
          endTime: this.normalizeTime(this.searchEndTime),
          reason: '管理后台按时间段软删除'
        })
        if (response.data.success && response.data.code === 200) {
          alert(response.data.message || '删除成功')
          this.selectedIdMap = {}
          this.pagination.number = 0
          this.loadRecordList()
        } else {
          throw new Error(response.data.message || '删除失败')
        }
      } catch (error) {
        console.error('按时间段删除失败:', error)
        alert(this.extractError(error, '按时间段删除失败'))
      }
    },
    normalizeTime(value) {
      if (!value) return null
      if (value.length === 16) {
        return value + ':00'
      }
      return value
    },
    formatAmount(amount) {
      return Number(amount || 0).toFixed(2)
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (isNaN(date.getTime())) return value
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },
    extractError(error, fallback) {
      if (error && error.response && error.response.data) {
        return error.response.data.message || `服务器错误: ${error.response.status}`
      }
      if (error && error.request) {
        return '网络连接失败，请检查网络或服务器状态'
      }
      return (error && error.message) || fallback
    }
  }
}
</script>

<style scoped>
.flow-records-admin {
  height: 100%;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  font-size: 24px;
  color: #333;
  margin: 0 0 8px 0;
}

.page-header p {
  color: #666;
  margin: 0;
  font-size: 14px;
}

.page-content {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.tab-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
  padding-bottom: 12px;
}

.tab-item {
  padding: 8px 18px;
  border: 1px solid #d9d9d9;
  border-radius: 999px;
  background: white;
  color: #333;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.tab-item:hover {
  border-color: #40a9ff;
  color: #1890ff;
}

.tab-item.active {
  background: #1890ff;
  border-color: #1890ff;
  color: white;
}

.search-section {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.search-box {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.store-select,
.date-input,
.search-input {
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
}

.store-select {
  background: white;
  cursor: pointer;
  min-width: 160px;
}

.date-input {
  min-width: 200px;
}

.search-input {
  min-width: 220px;
  flex: 1;
  max-width: 320px;
}

.store-select:focus,
.date-input:focus,
.search-input:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.check-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #555;
  user-select: none;
}

.search-btn,
.reset-btn {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: white;
  color: #333;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.search-btn {
  background: #1890ff;
  color: white;
  border-color: #1890ff;
}

.search-btn:hover {
  background: #40a9ff;
  border-color: #40a9ff;
}

.reset-btn:hover {
  background: #f5f5f5;
  border-color: #40a9ff;
}

.action-bar {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.danger-btn {
  padding: 8px 16px;
  border: 1px solid #ff4d4f;
  border-radius: 6px;
  background: #ff4d4f;
  color: white;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.danger-btn:hover:not(:disabled) {
  background: #ff7875;
  border-color: #ff7875;
}

.danger-btn:disabled {
  background: #f5f5f5;
  color: #bfbfbf;
  border-color: #d9d9d9;
  cursor: not-allowed;
}

.danger-btn.outline {
  background: white;
  color: #ff4d4f;
}

.danger-btn.outline:hover:not(:disabled) {
  background: #fff1f0;
}

.hint {
  color: #888;
  font-size: 12px;
}

.table-section {
  margin-bottom: 24px;
}

.table-header {
  margin-bottom: 16px;
}

.table-header h3 {
  font-size: 18px;
  color: #333;
  margin: 0;
}

.table-container {
  overflow-x: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.data-table th {
  background: #fafafa;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #f0f0f0;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  color: #666;
}

.data-row:hover {
  background: #f5f5f5;
}

.data-row.row-deleted {
  color: #bbb;
  background: #fafafa;
}

.data-row.row-deleted td {
  color: #bbb;
}

.summary-cell {
  max-width: 360px;
  white-space: pre-wrap;
}

.amount-cell {
  color: #52c41a;
  font-weight: 600;
}

.check-col {
  width: 44px;
  text-align: center;
}

.loading-cell,
.empty-cell {
  text-align: center;
  color: #999;
  font-style: italic;
}

.loading-spinner {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.active {
  background: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.status-badge.inactive {
  background: #fff1f0;
  color: #ff4d4f;
  border: 1px solid #ffa39e;
}

.row-delete-btn {
  padding: 4px 10px;
  border: 1px solid #ff4d4f;
  border-radius: 4px;
  background: white;
  color: #ff4d4f;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.2s ease;
}

.row-delete-btn:hover:not(:disabled) {
  background: #fff1f0;
}

.row-delete-btn:disabled {
  border-color: #d9d9d9;
  color: #bfbfbf;
  cursor: not-allowed;
}

.pagination-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 16px;
}

.pagination-info {
  color: #666;
  font-size: 13px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-btn {
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  font-size: 13px;
  color: #333;
  transition: all 0.2s ease;
}

.page-btn:hover:not(:disabled) {
  border-color: #40a9ff;
  color: #1890ff;
}

.page-btn:disabled {
  background: #f5f5f5;
  color: #bfbfbf;
  cursor: not-allowed;
}

.page-info {
  font-size: 13px;
  color: #666;
  min-width: 60px;
  text-align: center;
}
</style>
